/**
 * Mascot Domain — Djay Mascot System
 *
 * Barrel export for the Djay mascot components, hooks, animations, and messages.
 * DDD co-locatie: moved from components/mascot/ in s60.
 *
 * Usage:
 * ```tsx
 * import { DjayAvatar, DjayBubble, getDjayGreeting, djayVariants } from '../mascot'
 * ```
 */

// Core components
export { DjayAvatar, VARIANT_COLORS } from './DjayAvatar'
export type {
  DjaySize,
  DjayEmotion,
  DjayTier,
  DjayVariant,
  DjayAccessory,
  DjayAvatarProps,
} from './DjayAvatar'

// Intent components — the ONLY public entry points for product code.
// <DjayIdentity> mirrors the learner's look; <DjaySpecimen> shows a reward look;
// <DjayNarrator> stays neutral. Raw <DjayAvatar> is the render-primitive beneath.
// See docs/audits/MASCOT_SSOT_ARCHITECTURE_AUDIT_2026.md §5.
export { useDjayLook } from './useDjayLook'
export type { DjayLook } from './useDjayLook'
export { DjayIdentity } from './DjayIdentity'
export type { DjayIdentityProps } from './DjayIdentity'
export { DjaySpecimen } from './DjaySpecimen'
export type { DjaySpecimenProps, DjaySpecimenLook } from './DjaySpecimen'
export { DjayNarrator } from './DjayNarrator'
export type { DjayNarratorProps } from './DjayNarrator'

export { DjayBubble } from './DjayBubble'
export type { DjayBubbleProps, BubbleColorScheme } from './DjayBubble'

export { FloatingDjay } from './FloatingDjay'

// NOTE: DjayShowcase is intentionally NOT re-exported from this barrel.
// It is a 600+ line admin-only configurator and must be loaded via
// `React.lazy(() => import('../../../mascot/DjayShowcase'))` from
// `portals/admin/pages/MascotPage.tsx`. Exporting it through the barrel
// would defeat the code-split and re-bloat the learner bundle.

export { DjayMeesterLogo } from './DjayMeesterLogo'
export type { DjayMeesterLogoProps } from './DjayMeesterLogo'

export { BrandLockup } from './BrandLockup'
export type { BrandLockupProps } from './BrandLockup'

// DjayToetsAvatar: assessment-mode mascot (added to barrel during DDD audit
// Phase A4 to remove deep-import bypasses from assessment/).
export { DjayToetsAvatar } from './DjayToetsAvatar'

// Hooks
export { useDjaySelection } from './useDjaySelection'
export { useVariantUnlocks, TOTAL_VARIANTS } from './useVariantUnlocks'
export type {
  VariantUnlockInfo,
  VariantProgress,
  MascotRarity,
} from './useVariantUnlocks'
export { useDjayCustomization } from './useDjayCustomization'
export type {
  AccessoryUnlockInfo,
  TierUnlockInfo,
} from './useDjayCustomization'
export {
  useTreatmentUnlocks,
  TOTAL_TREATMENTS,
  TREATMENT_GATES,
} from './useTreatmentUnlocks'
export type { TreatmentUnlockInfo } from './useTreatmentUnlocks'
export { useLearnerContext } from './useLearnerContext'

// Unlock layer — one shared, first-class context contract (audit §9.4).
// `UnlockContext` is the canonical superset rooted on `AchievementCheckData`;
// the variant grid and the accessory/tier customization both read from it.
export type { UnlockContext } from './unlock/unlockContext'
// Unlock-persistence side-effect (domain-owned, drained out of CollectionPage).
export { usePersistUnlockedCosmetics } from './unlock/usePersistUnlockedCosmetics'
// Achievement-gate input selector — assembles AchievementCheckData from app
// state (drained out of CollectionPage; one named domain concern).
export { useAchievementCheckData } from './achievements/useAchievementCheckData'

// Config (timings + thresholds — single source of truth for magic numbers)
export { DJAY_TIMINGS, DJAY_THRESHOLDS, DJAY_TIME_EGGS } from './config'

// Messages / Copy Registry
export {
  getDjayGreeting,
  getDjayPersonalGreeting,
  getDjayDailyTip,
  getDjayThinkingMessage,
  getDjayMissionHeading,
  getDjayLabWelcome,
  getDjayModeIntro,
  getDjayViewMessage,
  getDjayIdleMessage,
  getDjaySplashMessage,
  getDjayDistractorIntro,
  getDjayExplanationIntro,
  getDjayWelcomeMessage,
  getDjayRegistrationStep,
} from './djayMessages'
export type { DjayMessage, LearnerContext } from './djayMessages'

// Easter Eggs (DDD co-locatie: mascot domain)
export { useEasterEggs, useLongPressDiscovered } from './useEasterEggs'
export {
  EASTER_EGGS,
  RAPID_TAP_ESCALATION,
  checkTimeBasedEgg,
  createRapidTapDetector,
  createKonamiDetector,
  getRapidTapReaction,
} from './easterEggs'
export type {
  EasterEggId,
  EasterEggReaction,
  EasterEggDefinition,
  EasterEggSound,
} from './easterEggs'

// Animations
export { djayVariants } from './animations/transitions'

// Illustrations
export { getAchievementIllustration } from './illustrations'
export { DjayIllustrationBase } from './illustrations'
export type { DjayIllustrationProps, AchievementRarity } from './illustrations'
